import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToastrService } from 'ngx-toastr';

import { BuyersListService } from '@core/services/buyers-list.service';
import { IBuyList } from '@shared/models/buyers-list';
import { hasShortMessage } from '@core/utils/premium-errors';

@UntilDestroy()
@Component({
  selector: 'app-property-share-buyers-list',
  templateUrl: './share-buyers-list.component.html',
  styleUrls: [
    './share-buyers-list.component.scss',
    '../share-emails/share-emails.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyShareBuyersListComponent implements OnInit {
  @Input({ required: true }) propertyId: string = '';

  protected buyersList: (IBuyList & {
    allowShareParsed: any;
    remainingTimeFormatted: string | null;
  })[] = [];
  protected loadingBuyersList = true;
  protected error = false;

  protected success = false;
  protected sending = false;
  protected selected = new Set<string>();

  constructor(
    private buyersListService: BuyersListService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.buyersListService
      .allBuyListFromOrganization({})
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (res) => {
          this.loadingBuyersList = false;
          this.error = false;
          this.buyersList = res.allBuyListFromOrganization.results.map(
            (item: any) => {
              try {
                const allowShareParsed = item.allowShare
                  ? JSON.parse(item.allowShare)
                  : null;

                let remainingTimeFormatted: string | null = null;

                if (
                  allowShareParsed &&
                  allowShareParsed['allow_share?'] === false
                ) {
                  const totalSeconds = allowShareParsed.remaining_time;
                  const hours = Math.floor(totalSeconds / 3600);
                  const minutes = Math.floor((totalSeconds % 3600) / 60);

                  remainingTimeFormatted = `${hours}h ${minutes}m`;
                }

                return {
                  ...item,
                  allowShareParsed,
                  remainingTimeFormatted,
                };
              } catch (e) {
                return {
                  ...item,
                  allowShareParsed: null,
                  remainingTimeFormatted: null,
                };
              }
            }
          );
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.loadingBuyersList = false;
          this.error = true;
          this.toastr.error(
            'Unable to load buyers list. Please try again later.',
            'Error'
          );
          this.cdr.detectChanges();
        },
      });
  }

  protected all_buy_lists_one_day_limit = false;
  shareWithList() {
    this.sending = true;
    this.success = false;
    this.buyersListService
      .sharePropertyWithBuyLists({
        buyLists: Array.from(this.selected),
        propertyId: this.propertyId,
      })
      .subscribe({
        next: (res) => {
          const reached_limit = hasShortMessage(
            res,
            'all_buy_lists_one_day_limit'
          );
          if (reached_limit) {
            this.sending = false;
            this.all_buy_lists_one_day_limit = true;
            this.cdr.detectChanges();
            return;
          }

          this.success = true;
          this.sending = false;
          this.toastr.success(
            'Property shared with buyers list successfully.',
            'Success'
          );
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.sending = false;
          this.toastr.error(
            'Unable to share property with buyers list. Please try again later.',
            'Error'
          );
          this.cdr.detectChanges();
        },
      });
  }

  toggleSelection(id: string, checked: boolean): void {
    if (checked) {
      this.selected.add(id);
    } else {
      this.selected.delete(id);
    }
  }

  trackByFn(index: number, item: IBuyList): string {
    return item.id;
  }
}
